import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { HistoryButton } from '@/features/audit-log';
import { ApiError } from '@/shared/api/api-error';
import { ErrorState } from '@/shared/components/error-state';
import { OrderReason } from '@/shared/components/order-reason';
import { SortableOrderList } from '@/shared/components/sortable-order-list';
import { StatusBadge } from '@/shared/components/status-badge';

import { useRegionOrder, useSaveRegionOrder } from '../api/regions.queries';
import type { RegionOrderItem } from '../types/region.types';

/**
 * The storefront region order (catalogue.manage, all-country scope): the order
 * of the store picker on a brand's page, in every country. The same editor as
 * the brand and category orders: drag a row by its handle (or with the
 * keyboard), or use its menu; give a reason; save.
 *
 * The list is shown exactly as the backend returns it, inactive regions
 * included (they keep their place if reactivated). Saving sends every id in
 * the new order; the backend validates, numbers them 1..n and audits it.
 * Mount only while open.
 */
export function RegionOrderDialog({ onClose }: { onClose: () => void }) {
  const current = useRegionOrder();
  // Owned here, not by the editor, so a refusal stays on screen after the
  // stale list is reloaded and the editor starts afresh.
  const save = useSaveRegionOrder();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-3 sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-1">
            <DialogTitle className="text-base">Storefront region order</DialogTitle>
            <HistoryButton
              entityType="RegionOrder"
              entityId="storefront"
              name="the storefront region order"
            />
          </div>
          <DialogDescription className="text-xs">
            The order of the stores customers choose from on a brand&apos;s page, in every country.
            A brand only shows the regions it has denominations in.
          </DialogDescription>
        </DialogHeader>
        {current.isLoading ? (
          <Skeleton className="h-48" />
        ) : current.error || !current.data ? (
          <ErrorState error={current.error} onRetry={() => void current.refetch()} />
        ) : (
          <OrderEditor
            // A refetch (e.g. after a refused, stale save) starts the editor afresh.
            key={current.dataUpdatedAt}
            initial={current.data}
            save={save}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function OrderEditor({
  initial,
  save,
  onClose,
}: {
  initial: RegionOrderItem[];
  save: ReturnType<typeof useSaveRegionOrder>;
  onClose: () => void;
}) {
  const [order, setOrder] = useState(initial);
  const [reason, setReason] = useState('');
  const originalIndex = new Map(initial.map((region, index) => [region.id, index]));
  const moved = order.filter((r, index) => originalIndex.get(r.id) !== index).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 text-xs">
      <SortableOrderList
        className="min-h-0 flex-1"
        listId="region-order"
        label="Region order"
        items={order}
        onChange={setOrder}
        originalIndex={originalIndex}
        itemLabel={(region) => region.name}
        disabled={save.isPending}
        renderItem={(region) => (
          <>
            <span className="truncate font-medium">{region.name}</span>
            <code className="text-muted-foreground font-mono text-[11px]">{region.code}</code>
            {region.isSystem && <StatusBadge variant="brand">System</StatusBadge>}
            {!region.isActive && <StatusBadge variant="neutral">Inactive</StatusBadge>}
          </>
        )}
      />
      <OrderReason id="region-order-reason" value={reason} onChange={setReason} />
      {save.error instanceof ApiError && (
        <p role="alert" className="text-destructive">
          {save.error.message}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <span className="text-muted-foreground me-auto" aria-live="polite">
          {moved === 0 ? 'No changes yet.' : `${moved} region${moved === 1 ? '' : 's'} moved.`}
        </span>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={moved === 0 || reason.trim().length < 3 || save.isPending}
          onClick={() =>
            save.mutate(
              { regionIds: order.map((r) => r.id), reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          Save order
        </Button>
      </div>
    </div>
  );
}
